<template>
    <div class="login-page">
        <div class="login-shell">
            <section class="brand-panel">
                <div class="brand-panel-inner">
                    <img
                        src="@/assets/icon/loginImage.svg"
                        alt="Login illustration"
                        class="brand-illustration"
                    />
                </div>
            </section>

            <section class="login-panel">
                <div class="login-card">
                    <div class="card-intro">
                        <h2 class="login-heading">Login to your account</h2>
                    </div>

                    <div class="form-field">
                        <label class="field-label" for="username"
                            >Email address / Username</label
                        >
                        <div class="input-shell">
                            <i
                                class="fa fa-user-o input-icon"
                                aria-hidden="true"
                            ></i>
                            <input
                                id="username"
                                v-model="userName"
                                type="text"
                                class="login-input"
                                autocomplete="username"
                                placeholder="you@company.com"
                                @keyup.enter="login"
                            />
                        </div>
                    </div>

                    <div class="form-field">
                        <label class="field-label" for="password"
                            >Password</label
                        >
                        <div class="input-shell">
                            <i
                                class="fa fa-lock input-icon"
                                aria-hidden="true"
                            ></i>
                            <input
                                id="password"
                                v-model="password"
                                :type="passwordInputType"
                                class="login-input password-input"
                                autocomplete="current-password"
                                placeholder="Enter your password"
                                @keyup.enter="login"
                            />
                            <button
                                type="button"
                                class="password-toggle-button"
                                @click="togglePasswordVisibility"
                                :aria-label="
                                    passwordInputType === 'password'
                                        ? 'Show password'
                                        : 'Hide password'
                                "
                            >
                                <i
                                    :class="
                                        passwordInputType === 'password'
                                            ? 'fa fa-eye'
                                            : 'fa fa-eye-slash'
                                    "
                                    aria-hidden="true"
                                ></i>
                            </button>
                        </div>
                    </div>

                    <div class="form-field" v-if="!isTenantSubdomain">
                        <label class="field-label">Login as</label>
                        <div class="role-grid">
                            <button
                                v-for="item in loginRoles"
                                :key="item.value"
                                type="button"
                                class="role-card"
                                :class="{
                                    'role-card--active': role === item.value,
                                }"
                                :aria-pressed="role === item.value"
                                @click="selectRole(item.value)"
                            >
                                <i :class="item.icon" aria-hidden="true"></i>
                                <span>{{ item.label }}</span>
                            </button>
                        </div>
                    </div>

                    <transition name="tenant-field">
                        <div
                            v-if="showTenantField"
                            class="form-field form-field--tenant"
                        >
                            <label class="field-label" for="tenant"
                                >Select tenant</label
                            >
                            <eb-select
                                id="tenant"
                                type="text"
                                class="login-select"
                                v-model="selectedTenant"
                                label="name"
                                :options="tenantList"
                                placeholder="Select tenant"
                            ></eb-select>
                        </div>
                    </transition>

                    <button
                        class="sign-in-btn"
                        :disabled="loading"
                        @click="login"
                    >
                        <span v-if="!loading">Log in</span>
                        <div
                            class="spinner-border btn-loader"
                            role="status"
                            v-else
                        >
                            <span class="sr-only">Loading...</span>
                        </div>
                    </button>

                    <div class="forgot-link-wrapper">
                        <router-link to="/recoverpw" class="forgot-link"
                            >Forgot password?</router-link
                        >
                    </div>

                    <div class="panel-divider"></div>

                    <div class="powered-by powered-by--panel">
                        <span class="powered-label powered-label--panel"
                            >Powered by</span
                        >
                        <img
                            src="@/assets/icon/nipigeLogo.svg"
                            alt="Powered by Nipige"
                            class="powered-logo powered-logo--panel"
                        />
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>
<script>
import { login } from "@/services/auth";
import ebSelect from "@/components/FormElements/Select.vue";
import "vue-select/dist/vue-select.css";
import { mapActions } from "vuex";

export default {
    data() {
        return {
            userName: "",
            password: "",
            role: "",
            loading: false,
            selectedTenant: null,
            domain: "",
            passwordInputType: "password",
            companyName: "NIPIGE",
            icon: "",
        };
    },
    components: {
        ebSelect,
    },
    methods: {
        ...mapActions({
            GetTenantList: "tenant/fetchTenantsListLite",
        }),
        validateEmail(email) {
            let re =
                /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return re.test(String(email).toLowerCase());
        },
        isPermission(perm) {
            return perm && perm.name;
        },
        validatePhone(phone) {
            let re = /^\d{10}$/;
            return re.test(String(phone));
        },
        isEmailOrPhone() {
            let code = 0;
            if (this.validateEmail(this.userName)) {
                code = 1;
            }
            if (this.validatePhone(this.userName)) {
                code = -1;
            }
            return code;
        },
        togglePasswordVisibility() {
            this.passwordInputType =
                this.passwordInputType === "password" ? "text" : "password";
        },
        selectRole(role) {
            this.role = role;
            if (role === "Admin" || role === "Tenant") {
                this.selectedTenant = null;
            }
        },
        async login() {
            const originalRole = this.role;
            const selectedUserTypeUpper = (this.role || "").toUpperCase();
            if (this.isTenantSubdomain) {
                this.role = "Tenant";
            }
            if (this.role.length > 0) {
                if (this.role !== "Admin" && this.role !== "Tenant") {
                    this.role = "Partner";
                }
                try {
                    if (this.userName) {
                        this.loading = true;
                        let code = this.isEmailOrPhone();
                        let opts = {
                            password: this.password,
                        };
                        if (this.role !== "Admin" && this.role !== "Tenant") {
                            opts.userType = selectedUserTypeUpper;
                        }
                        if (code === 1) {
                            opts.email = this.userName;
                        } else if (code == -1) {
                            opts.phone = this.userName;
                        } else {
                            opts.userName = this.userName;
                        }
                        sessionStorage.setItem("userRole", this.role);
                        if (this.role === "Partner") {
                            sessionStorage.setItem(
                                "nipige:key",
                                this.selectedTenant._id,
                            );
                        }
                        let response = await login(this.role, opts);
                        // check for force password update for tenant or partner.
                        if (
                            this.role === "Partner" &&
                            response.response.user.authentication
                                .forceChangePass
                        ) {
                            sessionStorage.setItem(
                                "nipige:forcechangepass",
                                JSON.stringify({
                                    type: "partner",
                                    token: response.response.token,
                                }),
                            );
                            this.$router.push("/changepassword");
                            return false;
                        } else if (
                            response.response.user.authentication
                                .forceChangePass &&
                            this.isTenantSubdomain &&
                            this.role === "Tenant"
                        ) {
                            sessionStorage.setItem(
                                "nipige:forcechangepass",
                                JSON.stringify({
                                    type: "Tenant",
                                    token: response.response.token,
                                }),
                            );
                            this.$router.push("/changepassword");
                            return false;
                        }
                        this.$store.dispatch("auth/setUserRole", this.role);
                        const { user, token } = response.response;
                        const { authorization, authentication, _id, address } =
                            user;
                        await this.$store.dispatch(
                            "permission/fetchPermissionsByUser",
                            {
                                token: token,
                                data: authorization.roles,
                            },
                        );
                        const permissions =
                            await this.$store.getters[
                                "permission/GetUserPermissions"
                            ];

                        let permissionObj = {};
                        for (let i = 0; i < permissions.length; i++) {
                            if (this.isPermission(permissions[i].permission)) {
                                permissionObj[permissions[i].permission.name] =
                                    1;
                            }
                        }
                        let userCatalog = [];
                        // If logged in with Tenant
                        if (this.role === "Tenant") {
                            sessionStorage.setItem("userName", this.userName);
                            this.$store.dispatch(
                                "auth/setCatalog",
                                response.response.user.agreement
                                    .businessSegmentInfo,
                            );
                            userCatalog =
                                response.response.user.agreement
                                    .businessSegmentInfo;
                            if (userCatalog.length > 0) {
                                this.$store.dispatch(
                                    "tenant/setTenantCatalogAccess",
                                    userCatalog[0]["categoryType"],
                                );
                            }
                            permissionObj["READ_FAQ"] = 1;
                            permissionObj["CREATE_SETUP"] = 1;
                            permissionObj["LIST_PARTNER_APPROVAL_REQUEST"] = 1;
                            permissionObj["GET_PARTNER_CUSTOMER_DETAILS"] = 0;
                            permissionObj["PAYMENT_RECORD"] = 1;
                        }
                        // If logged in with Admin
                        else if (this.role === "Admin") {
                            this.$store.dispatch("auth/setCatalog", []);
                            permissionObj["PARTNER_LIST"] = 0;
                            permissionObj["CREATE_ADMIN"] = 0;
                            permissionObj["CREATE_PARTNER"] = 0;
                            permissionObj[
                                "GET_ORDER_LIST_IN_ALL_STATES_BY_SELLER_BY_NO_OF_DAYS"
                            ] = 0;
                            permissionObj["PARTNER_LIST_LITE"] = 0;
                            permissionObj["CUSTOMER_SEARCH"] = 0;
                            permissionObj["GET_PARTNER_CUSTOMER_DETAILS"] = 0;
                            permissionObj["LIST_PARTNER_APPROVAL_REQUEST"] = 0;
                        }
                        // If logged in with Partners
                        else if (this.role === "Partner") {
                            permissionObj["READ_FAQ"] = 1;
                            permissionObj["CREATE_SETUP"] = 1;
                            if (
                                user.category !== "DISTRIBUTOR" &&
                                user.category !== "DEALER"
                            ) {
                                permissionObj["SUB_OFFICE_HIERARCHY"] = 1;
                                permissionObj["GET_PARTNER_CUSTOMER_DETAILS"] =
                                    0;
                            }
                            if (user.category === "DISTRIBUTOR") {
                                permissionObj["LIST_PARTNER_APPROVAL_REQUEST"] =
                                    1;
                                permissionObj["PAYMENT_RECORD"] = 1;
                            }
                            if (
                                user.category === "SELLER" &&
                                response.response.user.preferences
                                    .manageOwnCustomer
                            ) {
                                permissionObj["GET_PARTNER_CUSTOMER_DETAILS"] =
                                    1;
                            }
                            userCatalog =
                                response.response.user.agreement.marketSegment;
                            this.$store.dispatch(
                                "auth/setCatalog",
                                response.response.user.agreement.marketSegment,
                            );
                        }
                        const userObj = {
                            ...authentication,
                            _id,
                            catalogInfo: userCatalog,
                            permissions: permissionObj,
                            token,
                            role: this.role,
                            userLogo: "img/avatars/6.jpg",
                        };
                        this.loading = false;
                        if (this.role === "Partner") {
                            userObj.category = user.category;
                            userObj.tenant = response.response.user.tenant;
                            userObj.userLogo =
                                response.response.user.organization.logo.url;
                            if (response.response.user.parentPartner) {
                                userObj.parentPartner =
                                    response.response.user.parentPartner;
                            }
                        }
                        if (this.role === "Tenant") {
                            userObj.category = user.category;
                            userObj.userLogo =
                                response.response.user.company.logo;
                            userObj.enableManufacturer = user.preferences
                                .enableManufacturer
                                ? user.preferences.enableManufacturer
                                : false;
                            if (response.response.user.agreement) {
                                userObj.agreement =
                                    response.response.user.agreement;
                            }
                        }
                        if (
                            this.role === "Partner" &&
                            response.response.user.agreement
                        ) {
                            userObj.agreement =
                                response.response.user.agreement;
                        }
                        sessionStorage.setItem(
                            "userLogin",
                            JSON.stringify(userObj),
                        );
                        this.$store.dispatch("auth/loginUser", userObj);
                        if (this.isTenantSubdomain) {
                            this.$router.push("/profile/profile-info");
                        } else {
                            this.$router.push("/home");
                            // this.$router.push("/new-dashboard"); // for demo purpose
                        }
                    }
                } catch (error) {
                    this.role = originalRole;
                    this.$toasted.error(error.message.message);
                    this.loading = false;
                }
            } else {
                this.$toasted.error("please select login way");
            }
        },
        getTenantFromSubdomain() {
            return this.domain.split(".")?.shift();
        },
        async getTenantDetails() {
            if (!this.isTenantSubdomain) {
                return;
            }
            const payload = {
                subDomain: this.getTenantFromSubdomain(),
                // subDomain: "valley",
            };
            const tenant = await this.$store.dispatch(
                "tenant/getTenantDetailsBySubDomain",
                payload,
            );
            this.icon = tenant?.company?.logo || "";
            this.companyName = tenant?.company?.name || "";
            sessionStorage.setItem(
                "companyName",
                JSON.stringify(tenant?.company?.name),
            );
            sessionStorage.setItem(
                "theme",
                JSON.stringify(tenant.preferences.theme),
            );
            const primaryColor =
                tenant?.preferences?.theme?.primaryColor?.color;
            const secondaryColor =
                tenant?.preferences?.theme?.secondaryColor?.color;
            const tertiaryColor =
                tenant?.preferences?.theme?.tertiaryColor?.color;
            const fontColor = tenant?.preferences?.theme?.font?.color || "";
            // Set the value of variable
            document.documentElement.style.setProperty(
                "--primary",
                primaryColor,
            );
            document.documentElement.style.setProperty(
                "--secondary",
                secondaryColor,
            );
            document.documentElement.style.setProperty(
                "--tertiary",
                tertiaryColor,
            );
            document.documentElement.style.setProperty("--font", fontColor);
        },
    },
    computed: {
        selectOption() {
            return [
                "Admin",
                "Tenant",
                "Seller",
                "Distributor",
                "Dealer",
                "Manufacturer",
                "Sub Dealer",
            ];
        },
        tenantList() {
            let listdata = this.$store.getters["tenant/GetTenantLiteList"];
            listdata.map((object) => {
                object["name"] = object.company ? object.company.name : "";
            });
            return listdata;
        },
        isTenantSubdomain() {
            return this.domain?.indexOf(".web.nipige.com") != -1;
        },
        showTenantField() {
            return (
                !this.isTenantSubdomain &&
                !!this.role &&
                this.role !== "Admin" &&
                this.role !== "Tenant"
            );
        },
        loginRoles() {
            return [
                {
                    label: "Admin",
                    value: "Admin",
                    icon: "fa fa-shield",
                },
                {
                    label: "Tenant",
                    value: "Tenant",
                    icon: "fa fa-home",
                },
                {
                    label: "Seller",
                    value: "Seller",
                    icon: "fa fa-shopping-cart",
                },
                {
                    label: "Dealer",
                    value: "Dealer",
                    icon: "fa fa-users",
                },
                {
                    label: "Distributor",
                    value: "Distributor",
                    icon: "fa fa-truck",
                },
                {
                    label: "Manufacturer",
                    value: "Manufacturer",
                    icon: "fa fa-industry",
                },
                {
                    label: "Sub Dealer",
                    value: "Sub Dealer",
                    icon: "fa fa-id-badge",
                },
            ];
        },
        loginHighlights() {
            return [
                {
                    title: "Theme-aligned workspace",
                    copy: "A polished sign-in experience that inherits the app branding and tenant identity.",
                    icon: "fa fa-paint-brush",
                },
                {
                    title: "Operational access",
                    copy: "Move straight from sign in to dashboards, workflows, and execution tools with less friction.",
                    icon: "fa fa-line-chart",
                },
                {
                    title: "Role-aware security",
                    copy: this.isTenantSubdomain
                        ? "Tenant users land in a focused workspace tailored to their branded environment."
                        : "Admins, partners, and channel users can enter through the same controlled access layer.",
                    icon: "fa fa-shield",
                },
            ];
        },
    },
    mounted() {
        sessionStorage.clear();
        this.GetTenantList();
        // this.domain = "https://valley.dev.web.nipige.com/";
        this.domain = window.location.host;
        this.getTenantDetails();
    },
};
</script>
<style scoped>
.login-page {
    min-height: 100vh;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    padding: 0;
    position: relative;
    overflow-x: hidden;
    overflow-y: auto;
    font-family: "Plus Jakarta Sans", sans-serif;
    background: #ffffff;
}

.login-page::before,
.login-page::after {
    content: none;
}

.login-shell {
    width: 100%;
    height: 100vh;
    min-height: 100vh;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(28rem, 39rem);
    position: relative;
    z-index: 1;
}

.brand-panel,
.login-panel {
    height: 100vh;
    min-height: 100vh;
}

.brand-panel {
    position: relative;
    overflow: hidden;
    border-radius: 0;
    padding: clamp(2.5rem, 5vw, 4rem);
    background: linear-gradient(165deg, #dfeaff 0%, #edf3ff 48%, #d8e7ff 100%);
}

.brand-panel::before {
    content: "";
    position: absolute;
    width: 22rem;
    height: 22rem;
    border-radius: 50%;
    top: -6rem;
    left: -6rem;
    background: radial-gradient(
        circle,
        rgba(46, 116, 255, 0.12),
        transparent 70%
    );
    pointer-events: none;
}

.brand-panel::after {
    content: "";
    position: absolute;
    width: 18rem;
    height: 18rem;
    border-radius: 50%;
    bottom: -5rem;
    right: -5rem;
    background: radial-gradient(
        circle,
        rgba(46, 116, 255, 0.1),
        transparent 70%
    );
    pointer-events: none;
}

.brand-panel-inner {
    position: relative;
    z-index: 1;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.brand-illustration {
    width: 100%;
    max-width: min(34rem, 80%);
    height: auto;
    object-fit: contain;
    user-select: none;
    pointer-events: none;
}

.login-panel {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    overflow-y: auto;
    border-radius: 0;
    padding: clamp(2rem, 5vw, 4rem);
    background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
}

.login-card {
    width: 100%;
    max-width: 34rem;
}

.card-intro {
    margin-top: 2.5rem;
    margin-bottom: 2.25rem;
    text-align: center;
}

.login-heading {
    margin: 0;
    color: var(--slate-900);
    font-size: clamp(2rem, 2.6vw, 2.65rem);
    font-weight: 700;
    line-height: 1.08;
    letter-spacing: -0.03em;
}

.form-field {
    margin-bottom: 1.1rem;
}

.form-field--tenant {
    transform-origin: top;
}

.tenant-field-enter-active,
.tenant-field-leave-active {
    transition:
        opacity 0.18s ease,
        transform 0.18s ease;
}

.tenant-field-enter,
.tenant-field-leave-to {
    opacity: 0;
    transform: translateY(-0.35rem);
}

.field-label {
    display: block;
    margin-bottom: 0.65rem;
    color: #31486b;
    font-size: 0.95rem;
    font-weight: 700;
}

.input-shell {
    position: relative;
    transition: transform 0.18s ease;
}

.input-shell:focus-within {
    transform: translateY(-1px);
}

.input-icon {
    position: absolute;
    top: 50%;
    left: 1.05rem;
    transform: translateY(-50%);
    color: #90a4c1;
    font-size: 1.05rem;
    pointer-events: none;
    transition: color 0.18s ease;
}

.input-shell:focus-within .input-icon {
    color: var(--primary);
}

.login-input {
    width: 100%;
    height: 3.45rem;
    border-radius: 1rem;
    border: 1px solid #d7e1ee;
    background: #ffffff;
    color: var(--slate-800);
    font-size: 0.98rem;
    padding: 0.8rem 1rem 0.8rem 2.95rem;
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease,
        background-color 0.18s ease;
}

.login-input:-webkit-autofill,
.login-input:-webkit-autofill:hover,
.login-input:-webkit-autofill:focus,
.login-input:-webkit-autofill:active {
    -webkit-text-fill-color: var(--slate-800) !important;
    caret-color: var(--slate-800);
    border: 1px solid #d7e1ee !important;
    -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
    box-shadow: 0 0 0 1000px #ffffff inset !important;
    background-color: #ffffff !important;
    transition: background-color 9999s ease-in-out 0s;
}

.login-input:-moz-autofill,
.login-input:-moz-autofill:focus {
    border: 1px solid #d7e1ee !important;
    box-shadow: 0 0 0 1000px #ffffff inset !important;
    -moz-text-fill-color: var(--slate-800);
}

.login-input::placeholder {
    color: #7b8798;
}

.login-input:focus {
    outline: none;
    border-color: #2e74ff;
    box-shadow: 0 0 0 4px rgba(46, 116, 255, 0.12);
}

.password-input {
    padding-right: 3rem;
}

.password-toggle-button {
    position: absolute;
    top: 50%;
    right: 0.9rem;
    transform: translateY(-50%);
    border: none;
    background: transparent;
    color: #90a4c1;
    font-size: 1.05rem;
    padding: 0.25rem;
    transition: color 0.18s ease;
}

.password-toggle-button:hover {
    color: #2e74ff;
}

.role-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.75rem;
}

.role-card {
    min-height: 5.8rem;
    border: 1px solid #d7e1ee;
    border-radius: 1rem;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
    color: #476181;
    font-size: 0.9rem;
    font-weight: 600;
    text-align: center;
    padding: 0.75rem 0.6rem;
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease,
        transform 0.18s ease,
        background-color 0.18s ease,
        color 0.18s ease;
}

.role-card i {
    font-size: 1.3rem;
    color: #9aacc6;
    transition: color 0.18s ease;
}

.role-card:not(.role-card--active):hover {
    border-color: #b9cbe4;
    transform: translateY(-1px);
    box-shadow: 0 18px 28px -24px rgba(15, 23, 42, 0.3);
}

.role-card:focus {
    outline: none;
}

.role-card:focus:not(:focus-visible) {
    box-shadow: none;
}

.role-card:focus-visible {
    outline: none;
    border-color: #2e74ff;
    box-shadow: 0 0 0 4px rgba(46, 116, 255, 0.12);
}

.role-card--active {
    border-color: #2e74ff;
    background: rgba(46, 116, 255, 0.08);
    color: #1549cc;
    box-shadow: none;
    transform: none;
}

.role-card--active:hover,
.role-card--active:focus:not(:focus-visible) {
    border-color: #2e74ff;
    box-shadow: none;
    transform: none;
}

.role-card--active i {
    color: #2e74ff;
}

.password-toggle-button:focus {
    outline: none;
}

.sign-in-btn {
    width: 100%;
    height: 3.15rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 1rem;
    color: #ffffff;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    background: linear-gradient(135deg, #2e74ff 0%, #4583f3 100%);
    box-shadow: 0 18px 30px -20px rgba(15, 23, 42, 0.45);
    margin-top: 1.15rem;
    transition:
        transform 0.18s ease,
        box-shadow 0.18s ease,
        opacity 0.18s ease,
        filter 0.18s ease;
}

.sign-in-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 22px 34px -22px rgba(15, 23, 42, 0.52);
    filter: saturate(1.05);
}

.sign-in-btn:disabled {
    opacity: 0.8;
    cursor: not-allowed;
}

.btn-loader {
    width: 1.05rem;
    height: 1.05rem;
    border-width: 0.16em;
    margin: 0;
}

.forgot-link {
    color: #2e74ff;
    font-size: 0.96rem;
    font-weight: 600;
    text-decoration: none;
}

.forgot-link:hover {
    color: #155be5;
}

.forgot-link-wrapper {
    margin-top: 1rem;
    text-align: center;
}

.panel-divider {
    margin-top: 1.35rem;
    border-top: 1px solid #e8eef7;
}

.powered-by {
    display: flex;
    align-items: center;
    gap: 0.85rem;
}

.powered-by--panel {
    margin-top: 1rem;
    flex-direction: column;
    justify-content: center;
}

.powered-label {
    font-size: 0.82rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.powered-label--panel {
    color: #c3cedb;
}

.powered-logo {
    max-width: 100%;
}

.powered-logo--panel {
    width: 11rem;
}

::v-deep .login-select.form-group {
    margin: 0;
    cursor: pointer;
}

::v-deep .login-select .v-select {
    color: var(--slate-800);
}

::v-deep .login-select .vs__dropdown-toggle {
    min-height: 3.45rem;
    border-radius: 1rem;
    border: 1px solid #d7e1ee;
    background: #ffffff;
    padding: 0 0.65rem;
    display: flex;
    align-items: center;
    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease,
        transform 0.18s ease;
}

::v-deep .login-select .vs__dropdown-toggle:hover {
    border-color: #b9cbe4;
}

::v-deep .login-select .vs__selected-options {
    display: flex;
    align-items: center;
    padding: 0 0.45rem 0 0.65rem;
}

::v-deep .login-select .vs__selected,
::v-deep .login-select .vs__search,
::v-deep .login-select .vs__search::placeholder {
    color: #476181;
    font-size: 0.98rem;
    opacity: 1;
}

::v-deep .login-select .vs__selected {
    margin: 0;
    padding: 0;
    line-height: 1.25;
}

::v-deep .login-select .vs__search {
    margin: 0;
    padding: 0;
    line-height: 1.25;
}

::v-deep .login-select .vs__actions {
    padding: 0 0.42rem 0 0.25rem;
}

::v-deep .login-select .vs__open-indicator {
    fill: #90a4c1;
}

::v-deep .login-select .vs--open .vs__dropdown-toggle {
    border-color: #2e74ff;
    box-shadow: 0 0 0 4px rgba(46, 116, 255, 0.12);
    transform: translateY(-1px);
}

::v-deep .login-select .vs__dropdown-menu {
    margin-top: 0.55rem;
    border-radius: 1rem;
    border: 1px solid rgba(148, 163, 184, 0.22);
    background: #ffffff;
    color: var(--slate-800);
    box-shadow: 0 24px 38px -28px rgba(15, 23, 42, 0.45);
    overflow: hidden;
}

::v-deep .login-select .vs__dropdown-option {
    color: var(--slate-700);
    font-size: 0.95rem;
    padding: 0.65rem 0.9rem;
}

::v-deep .login-select .vs__dropdown-option--highlight {
    background: rgba(59, 130, 246, 0.1);
    color: var(--slate-900);
}

::v-deep .login-select .vs__dropdown-option:hover {
    background: rgba(59, 130, 246, 0.08);
    color: var(--slate-900);
}

::v-deep .login-select .vs__dropdown-option--selected {
    background: rgba(59, 130, 246, 0.14);
    color: var(--slate-900);
}

::v-deep .login-select .vs__clear {
    fill: var(--slate-400);
}

@media (max-width: 1080px) {
    .login-page {
        min-height: 100vh;
    }

    .login-shell {
        height: auto;
        grid-template-columns: 1fr;
    }

    .brand-panel,
    .login-panel {
        height: auto;
        min-height: auto;
    }

    .brand-panel {
        min-height: 34vh;
        padding: 2rem 1.5rem;
    }

    .login-panel {
        overflow-y: visible;
        padding: 2rem 1.5rem 2.5rem;
    }
}

@media (max-width: 767.98px) {
    .login-page {
        min-height: 100vh;
    }

    .brand-panel {
        display: none;
    }

    .login-panel {
        padding: 1.5rem 1rem 2rem;
    }

    .brand-illustration {
        max-width: min(18rem, 88%);
    }

    .login-card {
        max-width: none;
    }

    .card-intro {
        margin-top: 1.5rem;
        margin-bottom: 1.75rem;
    }

    .login-heading {
        font-size: 2rem;
    }

    .role-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .role-card {
        min-height: 5.6rem;
        gap: 0.55rem;
    }

    .login-input,
    ::v-deep .login-select .vs__dropdown-toggle {
        min-height: 3.05rem;
        height: 3.05rem;
        font-size: 0.94rem;
        border-radius: 0.9rem;
    }

    .login-input {
        padding: 0.7rem 0.95rem 0.7rem 2.7rem;
    }

    .password-input {
        padding-right: 2.8rem;
    }

    .password-toggle-button {
        right: 0.8rem;
    }

    .sign-in-btn {
        height: 3rem;
        border-radius: 0.9rem;
    }
}

@media (max-width: 420px) {
    .field-label,
    .powered-label {
        letter-spacing: 0.06em;
    }

    .login-panel {
        padding: 1rem;
    }

    .card-intro {
        margin-top: 1rem;
        margin-bottom: 1.5rem;
    }

    .role-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.65rem;
    }

    .role-card {
        min-height: 5.2rem;
        font-size: 0.88rem;
        padding: 0.75rem 0.55rem;
    }

    .role-card i {
        font-size: 1.2rem;
    }

    .powered-logo {
        width: 9.75rem;
    }
}

@media (max-height: 920px) and (min-width: 768px) {
    .login-page {
        min-height: 100vh;
    }

    .brand-panel,
    .login-panel {
        min-height: 100vh;
    }

    .login-panel {
        padding: 1.75rem 2rem;
    }

    .card-intro {
        margin-bottom: 1.5rem;
    }

    .login-heading {
        font-size: 2.2rem;
    }

    .form-field {
        margin-bottom: 0.9rem;
    }

    .login-input,
    ::v-deep .login-select .vs__dropdown-toggle {
        height: 3.1rem;
        min-height: 3.1rem;
    }

    .role-grid {
        gap: 0.65rem;
    }

    .role-card {
        min-height: 5.15rem;
        font-size: 0.86rem;
    }

    .role-card i {
        font-size: 1.15rem;
    }

    .sign-in-btn {
        height: 2.95rem;
        margin-top: 0.95rem;
    }

    .forgot-link-wrapper {
        margin-top: 0.85rem;
    }

    .panel-divider {
        margin-top: 1.1rem;
    }

    .powered-by--panel {
        margin-top: 0.9rem;
    }
}
</style>
